import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../SharedComponents/Pagination";
import { SearchInput } from "../SharedComponents/SearchInput";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { environment } from "../utils/environment";
import { calculateTotalPages } from "../utils/pagination";
import ArchiveGroupModal from "./ArchiveGroupModal/ArchiveModal";
import {
  IGroup,
  ManagedGroupsController,
  ManagedGroupsControllerDelegate,
} from "./controller";
import CreateGroupModal from "./CreateGroupModal/CreateGroupModal";
import ManagedGroupRow from "./ManagedGroupRow/ManagedGroupRow";

interface State {
  isLoading: boolean;
  groups: IGroup[];
  opendedCreateGroup: boolean;
  opendedArchiveGroup: boolean;
  archiveId: string;
}

interface Props {
  navigationHooks: NavigateFunction;
}

class Groups
  extends React.Component<Props, State>
  implements ManagedGroupsControllerDelegate {
  private controller: ManagedGroupsController;
  private filters = {
    filter: "",
    page: 1,
    total: 1,
    limit: 10,
  };

  constructor(props: Props) {
    super(props);
    this.controller = new ManagedGroupsController(this);
    this.state = {
      isLoading: false,
      groups: [],
      opendedCreateGroup: false,
      opendedArchiveGroup: false,
      archiveId: "",
    };
  }

  componentDidMount(): void {
    this.loadData();
  }

  // Delegate
  didGetGroups(groups: IGroup[]): void {
    this.setState({ groups, isLoading: false });
  }

  filter({
    page,
    total,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void {
    this.filters.page = page;
    this.filters.total = total;
  }

  shouldLogout() {
    logout();
  }

  genericError(error?: string): void {
    alert(error ?? "Erro na operação!");
  }

  didClickArchive(groupId: string) {
    this.setState({
      opendedArchiveGroup: true,
      archiveId: groupId,
    });
  }

  didClickCopyGroup(groupId: number) {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.getGroupFeedbackToken(token, groupId);
  }

  didGetFeedbackToken(token: string) {
    this.setState({
      isLoading: false,
    });
    const fullUrl = `${environment.groupFeedbackBaseUrl}?jwt=${token}`;
    window.open(fullUrl, "_newtab");
  }

  didArchiveGroup() {
    this.setState(
      {
        opendedArchiveGroup: false,
      },
      () => this.loadData()
    );
  }

  //Modals Delegate
  shouldCloseCreateModal() {
    this.setState({
      opendedCreateGroup: false,
    });
  }

  shouldCloseArchiveModal() {
    this.setState({
      opendedArchiveGroup: false,
    });
  }

  didCreateGroup() {
    this.setState(
      {
        opendedCreateGroup: false,
        isLoading: true,
      },
      () => {
        this.loadData();
      }
    );
  }

  // Internals
  didClickOpenCreateModal() {
    this.setState({
      opendedCreateGroup: true,
    });
  }

  loadData() {
    const token = localStorage.getItem("AccessToken")!;

    const newFilters: any = { ...this.filters };

    delete newFilters.total;

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === "") {
        delete newFilters[key];
      }
    });

    this.controller.getMyGroups(token, newFilters);
  }

  handlePageChange = (page: number) => {
    const totalPages =
      calculateTotalPages(this.filters.total, this.filters.limit) || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.filters.page) {
      return;
    }
    this.filters.page = nextPage;
    this.loadData();
  };

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderGroupRow() {
    return this.state.groups.map((group, id) => (
      <ManagedGroupRow group={group} delegate={this} key={`group_row_${id}`} />
    ));
  }

  renderGroupTable() {
    return (
      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Container
          maxWidth="lg"
          style={{
            padding: "80px 20px",
          }}
        >
          <Box
            marginBottom={2}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              gap: '0.75rem',
              alignItems: {
                sm: 'center'
              },
              justifyContent: {
                xs: 'space-between',
              },
              flexWrap: 'wrap'
            }}
          >
            <Stack direction="column" spacing={1.25}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h2">Devolutivas Agregadas</Typography>
                <Chip
                  label={`${this.filters?.total} ${this.filters?.total !== 1 ? "encontradas" : "encontrada"}`}
                  size="small"
                />
              </Box>

              <Typography variant="subtitle1" color="text.secondary">
                Reúna e acompanhe as aplicações
              </Typography>
            </Stack>

            <Box
              component={Button}
              variant="contained"
              flexWrap="wrap"
              onClick={() => this.didClickOpenCreateModal()}
            >
              Criar Devolutiva Agregada
            </Box>
          </Box>

          <Box
            marginBottom={2}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              gap: {
                xs: '1rem',
              },
              alignItems: {
                sm: 'center'
              },
              justifyContent: {
                xs: 'end',

              },
              flexWrap: 'wrap'
            }}
          >
            <SearchInput
              label="Pesquisar Devolutivas agregadas"
              onChangeFilter={(filter) => {
                this.filters.filter = filter;
              }}
              onSearch={(filter) => {
                this.filters.filter = filter;
                this.filters.page = 1;
                this.loadData();
              }}
            />

          </Box>

          <TableContainer component={Paper}>
            <Table aria-label="users-table">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Instrumento</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{this.renderGroupRow()}</TableBody>
            </Table>
          </TableContainer>

          <Pagination
            currentPage={this.filters.page}
            totalPages={calculateTotalPages(
              this.filters.total,
              this.filters.limit
            )}
            onChangePage={this.handlePageChange}
          />
        </Container>
      </Box>
    );
  }

  render() {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
        }}
      >
        {/* <Header logoutEnabled={ true }/> */}
        <Layout>
          {this.state.isLoading
            ? this.renderLoading()
            : this.renderGroupTable()}
          {this.state.opendedCreateGroup && (
            <CreateGroupModal delegate={this} />
          )}
          {this.state.opendedArchiveGroup && (
            <ArchiveGroupModal delegate={this} groupId={this.state.archiveId} />
          )}
        </Layout>
      </div>
    );
  }
}

export default withNavigationHooks(Groups);
